<template>
  <div class="row mx-0 px-3 py-4 border-top">
    <div class="input-group row pe-0">
      <div
        class="d-flex pe-0 justify-content-between justify-content-md-start align-items-center mb-1"
      >
        <label class="size-12" for="">希望勤務地</label>
        <span class="tag-thirdly size-10 ms-2">必須</span>
      </div>
    </div>
    <div
      v-if="prefectureError && baseInformationStore.profile_resume_desired_locations.length === 0"
      class="d-block invalid-feedback"
    >
      {{ prefectureError }}
    </div>
    <div class="input-group d-flex pe-0 gap-3">
      <div class="w-100 p-0 d-flex gap-0 flex-wrap">
        <div
          v-for="(prefecture, idx) in valuesPrefectures"
          :key="prefecture.id"
          class="mb-2 me-3 d-flex align-items-center p-0 mw-100"
        >
          <Field
            :id="`candidate_resume_prefectures_${idx}`"
            :name="`candidate_resume_prefectures[${idx}]`"
          >
            <div class="align-items-center d-flex flex-nowrap w-100">
              <p
                class="bg-silver-primary p-2 mb-0 border border-fourth rounded-2 me-1 flex-shrink-1 d-flex align-items-center"
              >
                {{ prefecture.name }}
                <template v-if="prefecture.cities.length > 0">
                  /
                  <template v-for="city in prefecture.cities" :key="city.id">
                    , {{ city.name }}
                  </template>
                </template>
                <img
                  src="@/assets/image/icon-close-delete.svg"
                  role="button"
                  class="ms-2 p-0d25"
                  @click="onRemovePrefecture(prefecture.id)"
                />
              </p>
            </div>
          </Field>
        </div>
      </div>
      <div class="d-flex justify-content-center align-items-center flex-column w-100 m-0">
        <button
          v-if="valuesPrefectures.length < maxItem"
          type="button"
          class="btn btn-outline-secondary p-0d8 d-flex justify-content-center align-items-center size-12 w-12 rounded-5 h-36x"
          @click="onOpenModalPrefecture"
        >
          <div class="icon w-1"></div>
          <span class="ms-2">希望勤務地を追加する</span>
        </button>
        <p class="d-flex justify-content-center text-secondary pe-2 pt-1 text-center">※ 3つまで</p>
      </div>
    </div>
  </div>

  <ModalBase ref="modalPrefecture" element="modalPrefectureElement" title="勤務地を選ぶ">
    <template #body>
      <div class="accordion accordion-primary flex-grow-1">
        <div
          v-for="(prefecture, index) in prefectures"
          id="prefecture-accordion"
          :key="prefecture.id"
          :class="`accordion accordion-item accordion-primary border-grey-10 d-flex border flex-column ${
            prefectures && index !== prefectures.length - 1 ? 'border-bottom-0' : ''
          }`"
        >
          <h4 :id="`prefecture-${prefecture.id}`" class="accordion-header px-3 py-2">
            <button
              type="button"
              class="rounded-0 bg-white accordion-button ps-3 size-14 text-decoration-none"
              :class="{ collapsed: prefecture.id !== KANTO_ID }"
              style="height: 56px"
              data-bs-toggle="collapse"
              :data-bs-target="`#prefecture-item-${prefectureAccordionSelected}-prefecture-${prefecture.id}`"
              aria-expanded="true"
              :aria-controls="`prefecture-item-${prefectureAccordionSelected}-prefecture-${prefecture.id}`"
            >
              {{ prefecture.name }}
            </button>
          </h4>
          <div
            :id="`prefecture-item-${prefectureAccordionSelected}-prefecture-${prefecture.id}`"
            class="accordion-body p-0 collapse"
            :class="{ show: prefecture.id === KANTO_ID }"
          >
            <div
              v-for="(children, childrenIndex) in prefecture.children"
              :key="children.id"
              class="ps-4 pe-3 py-2 border-bottom"
              :class="{ 'border-top': childrenIndex == 0 }"
            >
              <label class="form-check-label size-12 ms-3 d-flex align-items-center">
                <input
                  :id="`prefecture-item-input-${prefectureAccordionSelected}-children-${children.id}`"
                  type="checkbox"
                  class="form-check-input collapsed checkbox-primary-input w-1d5 h-1d5 mt-0"
                  :checked="valuesPrefectures.some((item) => item.id == children.id)"
                  :disabled="isInputDisabled(children.id)"
                  @change="onChangePrefecture(children)"
                />
                <label
                  class="form-check-label size-12 ms-3"
                  :for="`prefecture-item-input-${prefectureAccordionSelected}-children-${children.id}`"
                >
                  {{ children.name }}
                </label>
              </label>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect } from 'vue';
import { cloneDeep } from 'lodash';
import { PrefecturePrimary, PrefectureSecondary, PrefectureFifth } from '@/types/models/prefecture';
import { useBaseInformationStore } from '@/stores/resume-candidate/BasicInformation.store';
import { useFormErrorsStore } from '@/stores/resume-candidate/Errors.store';
import { getPrefectures, getProfile } from '@/apis/candidate';
import { Field } from 'vee-validate';
import ModalBase from '@/components/common/ModalBase/index.vue';
import { KANTO_ID } from '@/constant';

const prefectures = ref<PrefecturePrimary[]>([]);
const baseInformationStore = useBaseInformationStore();
const maxItem = 3;
const modalPrefecture = ref<InstanceType<typeof ModalBase> | null>(null);
const prefectureAccordionSelected = ref<number>(KANTO_ID);
const valuesPrefectures = ref<PrefecturePrimary[]>([]);
const formErrorsStore = useFormErrorsStore();
const prefectureError = ref('');

onMounted(() => {
  loadPrefectures();
  getDataProfile();
});

watchEffect(() => {
  prefectureError.value = formErrorsStore.errors.profile_resume_desired_locations || '';
});

const getDataProfile = async (): Promise<void> => {
  const response = await getProfile();
  if (response.status === 200) {
    setResumeDesiredLocation(response.data.candidate_resume_desired_locations);
  }
};

const setResumeDesiredLocation = (data: PrefectureFifth): void => {
  valuesPrefectures.value = data.reduce((prefecture: PrefecturePrimary[], item) => {
    const existingPrefecture = prefecture.find((p) => p.id === item.prefecture_id);
    if (existingPrefecture) {
      if (item.city_id !== null) {
        existingPrefecture.cities.push({
          id: item.city_id,
          name: item.city_name,
        });
      }
    } else {
      const cities =
        item.city_id !== null
          ? [
              {
                id: item.city_id,
                name: item.city_name,
              },
            ]
          : [];

      prefecture.push({
        id: item.prefecture_id,
        name: item.prefecture_name,
        parentId: item.parent_id,
        parentName: item.parent_name,
        cities: cities,
      });
    }
    return prefecture;
  }, []);
};

const loadPrefectures = async (): Promise<void> => {
  const response = await getPrefectures();
  if (response.status === 200) prefectures.value = response.data;
};

const onOpenModalPrefecture = (): void => {
  modalPrefecture.value?.onOpen();
};

const onChangePrefecture = (prefecture: PrefectureSecondary): void => {
  const prefectureIndex = valuesPrefectures.value.findIndex((item) => item.id === prefecture.id);
  if (prefectureIndex !== -1) {
    valuesPrefectures.value.splice(prefectureIndex, 1);
  } else {
    valuesPrefectures.value.push({
      id: prefecture.id,
      name: prefecture.name,
      cities: [],
      parentId: prefecture.parentId,
      parentName: prefecture.parentName,
    });
  }

  prefectures.value.forEach((item) => {
    item.children?.forEach((child) => {
      if (child.id === prefecture.id) {
        child.selected = prefectureIndex === -1;
      }
    });
  });

  updateDesiredLocations();
};

const updateDesiredLocations = (): void => {
  const desiredLocations: PrefectureFifth[] = valuesPrefectures.value.flatMap((pref) =>
    pref.cities.length > 0
      ? pref.cities.map((city, index) => ({
          id: index,
          prefecture_id: pref.id,
          city_id: city.id,
        }))
      : [{ prefecture_id: pref.id }]
  );

  baseInformationStore.profile_resume_desired_locations = cloneDeep(desiredLocations);
};

const onRemovePrefecture = (prefectureId: number): void => {
  valuesPrefectures.value = valuesPrefectures.value.filter((item) => item.id !== prefectureId);
  updateDesiredLocations();
};

const isInputDisabled = (prefectureId: number, cityId?: number): boolean => {
  const isPrefectureSelected = valuesPrefectures.value.some((item) => item.id === prefectureId);

  if (cityId) {
    if (isPrefectureSelected) {
      return false;
    }
    return valuesPrefectures.value.length >= maxItem;
  }

  if (isPrefectureSelected) {
    return false;
  }

  return valuesPrefectures.value.length >= maxItem;
};
</script>
